/**
 * Cart Custom - Free gifts, upsell add-to-cart, and cart section refresh
 * Extracted from theme.liquid for performance and maintainability.
 */
(function () {
  'use strict';

  const FREE_GIFT_SELECTORS = ['.free-gift', '.free-gift-1', '.free-gift-2', '.free-gift-3'];

  /**
   * Fetches the cart section HTML and replaces the DOM. Used after add/remove/change.
   * @returns {Promise<void>}
   */
  function refreshCartSection() {
    const cartComponent = document.querySelector('cart-items-component');
    if (!cartComponent) return Promise.resolve();

    const sectionId = cartComponent.getAttribute('data-section-id');
    if (!sectionId) return Promise.resolve();

    return fetch(`/?section_id=${sectionId}`)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newContent = doc.querySelector(`[data-section-id="${sectionId}"]`);
        const cartItems = document.querySelector(`[data-section-id="${sectionId}"]`);
        if (cartItems && newContent) {
          cartItems.innerHTML = newContent.innerHTML;
        }
        document.dispatchEvent(new CustomEvent('cart:update', { detail: { data: {} } }));
      })
      .catch(() => {});
  }

  /**
   * Adds a variant to the cart via fetch, then refreshes the cart section.
   * @param {string} variantId - Shopify variant ID
   * @param {number} quantity - Quantity to add
   * @returns {Promise<void>}
   */
  function addToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && data.status >= 400) throw new Error(data.message || 'Add to cart failed');
        return refreshCartSection();
      });
  }

  /**
   * Removes a variant from the cart via fetch, then refreshes the cart section.
   * @param {string} variantId - Shopify variant ID
   * @returns {Promise<void>}
   */
  function removeFromCart(variantId) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: variantId, quantity: 0 }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && data.status >= 400) throw new Error(data.message || 'Remove from cart failed');
        return refreshCartSection();
      });
  }

  /**
   * Evaluates a single free gift: add if eligible, remove if not.
   * @param {string} giftSelector - CSS selector for the free gift element
   * @returns {Promise<void>}
   */
  function processFreeGift(giftSelector) {
    const giftElement = document.querySelector(giftSelector);
    if (!giftElement) return Promise.resolve();

    const variantId = giftElement.getAttribute('data-variant-id');
    const threshold = parseInt(giftElement.getAttribute('data-threshold') || '0', 10);
    if (!variantId || isNaN(threshold)) return Promise.resolve();

    return fetch('/cart.js')
      .then((res) => res.json())
      .then((cartData) => {
        const subtotal = cartData.total_price;
        const freeGiftExists = (cartData.items || []).some((item) => String(item.variant_id) === String(variantId));

        if (subtotal >= threshold) {
          if (!freeGiftExists) return addToCart(variantId, 1);
        } else if (freeGiftExists) {
          return removeFromCart(variantId);
        }
        return Promise.resolve();
      })
      .catch(() => {});
  }

  /**
   * Processes all free gift selectors. Called after cart:update and after each add/remove.
   */
  function processAllFreeGifts() {
    FREE_GIFT_SELECTORS.forEach((sel) => processFreeGift(sel));
  }

  /**
   * Handles upsell variant dropdown change: updates button data-variant-id and price.
   */
  /** @param {Event} e */
  function onUpsellVariantChange(e) {
    if (!e.target.classList.contains('upsell-product__variants')) return;

    const select = e.target;
    const selectedVariantId = select.value;
    const buttonId = select.getAttribute('data-button-id');
    const option = select.options[select.selectedIndex];
    const price = option ? option.getAttribute('data-price') : '';

    const container = select.closest('.upsell-product');
    if (!container) return;

    const button = container.querySelector(`#${buttonId}`);
    const priceDiv = container.querySelector('.carts-up.upsell-product__price');

    if (button) button.setAttribute('data-variant-id', selectedVariantId);
    if (priceDiv && price) priceDiv.innerHTML = price;
  }

  /**
   * Handles upsell add-to-cart click: adds variant and refreshes cart.
   */
  /** @param {Event} e */
  function onUpsellAddToCart(e) {
    const button = e.target.closest('.upsell-add-to-cart');
    if (!button) return;

    e.preventDefault();

    const variantId = button.getAttribute('data-variant-id');
    const cartComponent = document.querySelector('cart-items-component');
    const sectionId = cartComponent ? cartComponent.getAttribute('data-section-id') : null;

    if (!variantId) return;

    const btnText = button.querySelector('span.btn-text');
    /** @param {string} text - Button text @param {boolean} disabled - Disabled state */
    const setButtonState = (text, disabled) => {
      button.disabled = disabled;
      if (btnText) btnText.textContent = text;
      else button.textContent = text;
    };

    setButtonState('Adding...', true);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((res) => res.json())
      .then((data) => {
        setButtonState('Quick Add', false);
        if (data.status && data.status >= 400) throw new Error(data.message);
        if (sectionId) return refreshCartSection();
      })
      .catch(() => {
        setButtonState('Add to Cart', false);
      });
  }

  function init() {
    document.addEventListener('cart:update', () => processAllFreeGifts());
    document.addEventListener('change', onUpsellVariantChange);
    document.addEventListener('click', onUpsellAddToCart);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
